import clsx from 'clsx'
import PropTypes from 'prop-types'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'

import {
  ChartBarIcon,
  ChartPieIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  CogIcon,
  CpuChipIcon,
  InformationCircleIcon as InformationCircleIconOutline,
  NewspaperIcon,
  QuestionMarkCircleIcon,
  RectangleStackIcon,
  SparklesIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'

import AivyIcon from '../assets/images/aivyicon.svg'
import trackEvents from '../constants/track-events'
import { usePartnerContext, useUserContext } from '../context'
import { trackEvent } from '../helpers/analytics'
import FeatureBadge from './feature-badge'
import ProfileDropdown from './profile-dropdown'
import SpaceSwitch from './space-switch'

const TOOLTIP_ID = 'sidebar-tooltip'

const IS_MAC =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.userAgent)

// Geometrie: Die Sidebar hat px-3, jede Zeile startet mit einer 40 px breiten
// Icon-Spalte. Eingeklappt ist sie 64 px breit, die Icons stehen also in beiden
// Zuständen an derselben Stelle und springen beim Umschalten nicht. Texte
// werden nur ausgeblendet, damit Screenreader die Namen weiter vorlesen.
// Standort-Switch und Profil-Menü folgen demselben Raster.
const labelVisibility = (isSidebarCollapsed) =>
  clsx(
    'transition-opacity duration-200 motion-reduce:transition-none',
    isSidebarCollapsed && 'opacity-0'
  )

const Divider = () => (
  <div className='mx-2.5 my-3 h-px bg-white/10' aria-hidden='true' />
)

const Sidebar = ({ isSidebarCollapsed, onToggleCollapse, actions }) => {
  const { t } = useTranslation()
  const { activeSpace, employees, isAdmin } = useUserContext()
  const { partner } = usePartnerContext()
  // const { adminInsights, toggleAdminInsights } = useAivyContext()

  const { pathname } = useLocation()

  const { setIsVisibleHelpAndSupport, setVisibilityChooseGroup } = actions

  const {
    create: can_create,
    update: can_update,
    delete: can_delete
  } = activeSpace.authorization_role.crud

  const { hasCompletedOnboarding } = activeSpace || {}

  const isDiscoverVisible = partner.isDashboardComponentVisible.discover

  const navigation = [
    [
      ...(isDiscoverVisible
        ? [
            {
              id: 'link-to-discover',
              name: t('components.layout.navigation_discover'),
              onClick: () => trackEvent(trackEvents.PRESSED_TALENTS),
              to: '/discover',
              current: pathname.includes('discover'),
              icon: SparklesIcon
            }
          ]
        : [])
    ],
    ...(!isDiscoverVisible || hasCompletedOnboarding
      ? [
          [
            {
              id: 'link-to-talents',
              name: t('components.layout.navigation_talents'),
              onClick: () => trackEvent(trackEvents.PRESSED_TALENTS),
              to: '/talents',
              current: pathname.includes('talent'),
              icon: UserGroupIcon
            },
            {
              id: 'link-to-careers',
              name: t('components.layout.navigation_careers'),
              onClick: () => trackEvent(trackEvents.PRESSED_CAREERS),
              to: '/careers',
              current:
                pathname.includes('career') &&
                !pathname.includes('sample_talent') &&
                !pathname.includes('discover'),
              icon: RectangleStackIcon
            },
            {
              id: 'link-to-team-analyses',
              name: t('components.layout.navigation_teamanalyses'),
              onClick: () => trackEvent(trackEvents.PRESSED_TEAMANALYSES),
              to: '/team-analyses',
              current: pathname.includes('team-analyse'),
              icon: ChartPieIcon
            },
            // Interne Testphase: Nutzungsstatistik vorerst nur für __admin
            // sichtbar; die Seite selbst bleibt erreichbar, das Backend
            // liefert Nicht-Admins ohnehin nur die Kompakt-Version.
            ...(isAdmin
              ? [
                  {
                    id: 'link-to-usage',
                    name: t('components.layout.navigation_usage'),
                    to: '/usage',
                    current: pathname === '/usage',
                    icon: ChartBarIcon
                  }
                ]
              : [])
          ],
          [
            // eslint-disable-next-line
            can_create && can_update && can_delete && false
              ? {
                  name: t('components.layout.navigation_producttour'),
                  to: '/careers',
                  state: { startProductTour: true },
                  replace: pathname === '/careers',
                  onClick: () => trackEvent(trackEvents.PRESSED_PRODUCTTOUR),
                  current: false,
                  icon: InformationCircleIconOutline,
                  className: 'hidden xl:flex'
                }
              : null,
            {
              id: 'nav_support_hub',
              name: t('components.layout.navigation_support'),
              onClick: () => {
                trackEvent(trackEvents.PRESSED_HELP_SUPPORT)
                setIsVisibleHelpAndSupport(true)
              },
              current: false,
              icon: QuestionMarkCircleIcon
            },
            {
              id: 'settings',
              name: t('components.layout.navigation_settings'),
              to: '/settings/personal',
              current: pathname.startsWith('/settings'),
              icon: CogIcon
            },
            isAdmin && {
              id: 'link-to-internal-updates',
              name: 'Interne Updates',
              to: '/internal/updates',
              current: false,
              icon: NewspaperIcon
            },
            isAdmin && {
              name: 'Gruppe wechseln',
              onClick: () => setVisibilityChooseGroup(true),
              current: false,
              icon: CpuChipIcon
            }
          ].filter((item) => item)
        ]
      : [])
  ]

  // Ohne Entdecken ist die erste Gruppe leer, sonst stünde ganz oben ein
  // Trennstrich ohne Einträge davor
  const sections = navigation.filter((section) => section.length > 0)

  const shortcut = IS_MAC ? '⌘B' : `${t('components.layout.ctrl_key')}+B`
  const toggleLabel = t(
    isSidebarCollapsed
      ? 'components.layout.sidebar_expand'
      : 'components.layout.sidebar_collapse'
  )

  const renderNavigationItem = (item) => {
    const className = clsx(
      'group flex h-9 w-full items-center gap-x-3 overflow-hidden rounded-md px-2.5 text-left text-sm font-medium transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brightBlue',
      item.current
        ? 'bg-white/10 text-white'
        : 'text-gray-400 hover:bg-white/5 hover:text-white',
      item.className
    )

    const content = (
      <>
        <item.icon className='size-5 shrink-0' aria-hidden='true' />
        <span
          className={clsx(
            'whitespace-nowrap',
            labelVisibility(isSidebarCollapsed)
          )}
        >
          {item.name}
        </span>
        {item.badge && !isSidebarCollapsed && <FeatureBadge id={item.badge} />}
      </>
    )

    // Eingeklappt sind die Namen unsichtbar, dann übernimmt der Tooltip
    const tooltipProps = {
      'data-tooltip-id': isSidebarCollapsed ? TOOLTIP_ID : undefined,
      'data-tooltip-content': item.name
    }

    if (item.to) {
      return (
        <Link
          id={item.id}
          to={item.to}
          state={item.state}
          replace={item.replace}
          onClick={item.onClick}
          aria-current={item.current ? 'page' : undefined}
          className={className}
          {...tooltipProps}
        >
          {content}
        </Link>
      )
    }

    return (
      <button
        id={item.id}
        type='button'
        onClick={item.onClick}
        className={className}
        {...tooltipProps}
      >
        {content}
      </button>
    )
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col overflow-hidden bg-gray-900'>
      <div className='flex h-16 shrink-0 items-center px-3'>
        {onToggleCollapse && isSidebarCollapsed ? (
          // Eingeklappt wird das Logo selbst zum Ausklappen-Button, beim
          // Hovern zeigt es den Pfeil (wie Claude oder ChatGPT)
          <button
            type='button'
            onClick={onToggleCollapse}
            aria-label={toggleLabel}
            aria-expanded={false}
            data-tooltip-id={TOOLTIP_ID}
            data-tooltip-content={`${toggleLabel} (${shortcut})`}
            className='group/toggle relative flex size-10 items-center justify-center rounded-md transition-colors hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brightBlue'
          >
            <img
              className='h-7 w-auto transition-opacity group-hover/toggle:opacity-0 group-focus-visible/toggle:opacity-0'
              src={AivyIcon}
              alt='Aivy GmbH'
            />
            <ChevronDoubleRightIcon
              className='absolute size-5 text-gray-300 opacity-0 transition-opacity group-hover/toggle:opacity-100 group-focus-visible/toggle:opacity-100'
              aria-hidden='true'
            />
          </button>
        ) : (
          <>
            <div className='flex size-10 shrink-0 items-center justify-center'>
              <img className='h-7 w-auto' src={AivyIcon} alt='Aivy GmbH' />
            </div>
            {onToggleCollapse && (
              <button
                type='button'
                onClick={onToggleCollapse}
                aria-label={toggleLabel}
                aria-expanded={true}
                data-tooltip-id={TOOLTIP_ID}
                data-tooltip-content={`${toggleLabel} (${shortcut})`}
                className='ml-auto flex size-8 shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brightBlue'
              >
                <ChevronDoubleLeftIcon className='size-5' aria-hidden='true' />
              </button>
            )}
          </>
        )}
      </div>

      <div className='flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden px-3 pb-4'>
        <nav>
          {sections.map((section, sectionIdx) => (
            <Fragment key={sectionIdx}>
              {sectionIdx > 0 && <Divider />}
              <ul role='list' className='space-y-0.5'>
                {section.map((item) => (
                  <li key={item.id || item.name}>
                    {renderNavigationItem(item)}
                  </li>
                ))}
              </ul>
            </Fragment>
          ))}
        </nav>
        {employees.length > 1 && (
          <div className='mt-6'>
            <div
              className={clsx(
                'whitespace-nowrap px-2.5 text-xs/6 font-medium text-gray-400',
                labelVisibility(isSidebarCollapsed)
              )}
            >
              {t('components.layout.companys_area_title')}
            </div>
            <div className='mt-1'>
              <SpaceSwitch isSidebarCollapsed={isSidebarCollapsed} />
            </div>
          </div>
        )}
      </div>

      <div className='shrink-0 border-t border-white/10 p-3'>
        <ProfileDropdown isSidebarCollapsed={isSidebarCollapsed} />
      </div>

      <Tooltip
        id={TOOLTIP_ID}
        place='right'
        offset={20}
        positionStrategy='fixed'
        className='z-50 rounded-md text-xs font-medium'
        style={{ backgroundColor: '#182033' }}
      />
    </div>
  )
}

Sidebar.propTypes = {
  isSidebarCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
  actions: PropTypes.object.isRequired
}

Sidebar.defaultProps = { isSidebarCollapsed: false, onToggleCollapse: null }

export default Sidebar
